import { AppComponent, Browser, BrowserEvent, BrowserState, ComponentState } from "@reporting/rp-app-host";
import { html } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { Accordion, AccordionItemState, ComponentId, ComponentStore, MenuEvent, dom } from "@omegagrid/core";
import { style } from "./sidebar.style";
import { ProjectTreeAdapter, FileBrowserAdapter, QueryBrowserAdapter, isForeignProject, projectCaption } from "../adapters";
import { msg } from "@omegagrid/localize";
import { Project } from "@reporting/rp-types/ext";
import { TreeDropdown, TreeEvent } from "@omegagrid/tree";
import { ExtensionApplication } from "..";
import rp from "@reporting/rp-app-host";
import { EntityAccessType } from "@reporting/rp-types/users";

type SidebarState = {
	idProject?: number;
	components?: Record<string, ComponentState>;
	accordion?: AccordionItemState[];
}

@customElement('rp-mf-ext-sidebar')
export class Sidebar extends AppComponent<ExtensionApplication, SidebarState> {

	static styles = [style];

	@query('og-accordion')
	accordion: Accordion;

	@query('.project-select')
	projectSelect: TreeDropdown;

	@state()
	project: Project;

	get idProject() { return this.project?.id_custom_page }

	private _projectTreeAdapter: ProjectTreeAdapter;
	get projectTreeAdapter() {
		if (this._projectTreeAdapter) return this._projectTreeAdapter;
		this._projectTreeAdapter = new ProjectTreeAdapter({newProject: true});
		return this._projectTreeAdapter;
	}

	async openProject(idProject: number) {
		this.browserComponentStore.clear();
		this.project = await rp.ms('extensions').get<Project>(`projects/${idProject}`);
		this.app.activeProject = this.project;

		if (this.state?.idProject !== idProject) {
			this.updateState({idProject: this.project ? idProject : null}, true);
		}
	}

	async closeProject() {
		this.project = null;
		this.app.activeProject = null;
		this.updateState({idProject: null}, true);
	}

	async updated() {
		if (this.state?.idProject && this.state?.idProject !== this.idProject) {
			await this.openProject(this.state?.idProject);
		}

		if (this.project && this.projectSelect) {
			this.projectSelect.value = [[String(this.idProject), 0, 1, {v: projectCaption(this.project)}]];
		}
	}

	browserComponentStore = new ComponentStore<Browser>((id: ComponentId) => {
		const browser = dom.createElement<Browser>('rp-browser');
		browser.addEventListener('browser.select', this._onSelect);
		browser.addEventListener('browser.state', this._onChangeState);
		switch (id) {
			case 'queries':
				browser.adapter = new QueryBrowserAdapter(this.idProject);
				browser.state = this.state?.components?.queryBrowser as BrowserState;
				break;
			case 'files':
				browser.adapter = new FileBrowserAdapter(this.idProject);
				browser.state = this.state?.components?.fileBrowser as BrowserState;
				break;
		}
		return browser;
	});

	layout() {
		this.accordion?.layout();
	}

	_onSelect = (e: BrowserEvent) => {
		switch (e.node.key) {
			case 'extensions-fileForm':
				this.host.openModalSidebar({id: e.node.key, caption: msg('New file')});
				return;
			case 'extensions-queryForm':
				this.host.openModalSidebar({id: e.node.key, caption: msg('New query')});
				return;
			default:
				this.host.open(e.node.key, {title: e.node.value});
				return;
		}
	}

	_onChangeState = () => {
		this.dispatchStateEvent();
	}

	/** Applies the persisted accordion state (collapsed + size) onto freshly built items, matched by id. */
	_restoreAccordionItems = <T extends {id?: ComponentId}>(items: T[]): T[] => {
		const saved = this.state?.accordion;
		if (!saved) return items;
		return items.map(item => {
			const s = saved.find(x => x.id === item.id);
			return s ? {...item, collapsed: s.collapsed, size: s.size} : item;
		});
	}

	getState() {
		return {
			name: 'sidebar',
			idProject: this.idProject,
			components: {
				queryBrowser: this.browserComponentStore.items.get('queries')?.getState(),
				fileBrowser: this.browserComponentStore.items.get('files')?.getState(),
			},
			accordion: this.accordion?.getItemsState(),
		}
	}

	toggleCollapse = async (id: ComponentId) => {
		(await this.browserComponentStore.get(id))?.tree?.toggleTreeCollapse();
	};

	refresh = async (id?: ComponentId) => {
		if (id == 'projects') {
			this._projectTreeAdapter = null;
			if (this.projectSelect) {
				this.projectSelect.adapter = this.projectTreeAdapter; // force reload
			}
		}
		else if (id) (await this.browserComponentStore.get(id))?.refresh();
		else this.browserComponentStore.forEach(b => b.refresh());
	};

	_onProjectChange = async (e: TreeEvent) => {
		if (e.node?.key == '0') {
			rp.host.openModalSidebar({id: 'extensions-projectForm', caption: msg('New project')});
			return;
		} else if (e.node?.key) this.openProject(Number(e.node.key));
		else this.closeProject();
	};

	renderFilesTitle = () => html`
		<div style="display: flex; align-items: center; gap: 5px;">
			<div style="flex: 1">${msg('Client')}</div>
			<div style="flex: 0; display: flex" @click="${(e: PointerEvent) => e.stopImmediatePropagation()}">
				<og-button
					icon="plus"
					title="${msg('New file')}"
					color="transparent"
					style="padding: 0 5px;"
					@click="${(e: PointerEvent) => this.host.openModalSidebar({id: 'extensions-fileForm', caption: msg('New file')})}">
				</og-button>

				<og-button
					icon="arrows-rotate"
					title="${msg('Refresh')}"
					color="transparent"
					style="padding: 0 5px;"
					@click="${(e: PointerEvent) => this.refresh('files')}">
				</og-button>

				<og-button
					icon="square-plus"
					title="${msg('Collapse/expand tree')}"
					color="transparent"
					style="padding: 0 5px;"
					@click="${(e: PointerEvent) => this.toggleCollapse('files')}">
				</og-button>
			</div>
		</div>
	`;

	renderQueriesTitle = () => html`
		<div style="display: flex; align-items: center; gap: 5px;">
			<div style="flex: 1">${msg('Server')}</div>
			<div style="flex: 0; display: flex" @click="${(e: PointerEvent) => e.stopImmediatePropagation()}">
				<og-button
					icon="plus"
					title="${msg('New query')}"
					color="transparent"
					style="padding: 0 5px;"
					@click="${(e: PointerEvent) => this.host.openModalSidebar({id: 'extensions-queryForm', caption: msg('New query')})}">
				</og-button>

				<og-button
					icon="arrows-rotate"
					title="${msg('Refresh')}"
					color="transparent"
					style="padding: 0 5px;"
					@click="${(e: PointerEvent) => this.refresh('queries')}">
				</og-button>

				<og-button
					icon="square-plus"
					title="${msg('Collapse/expand tree')}"
					color="transparent"
					style="padding: 0 5px;"
					@click="${(e: PointerEvent) => this.toggleCollapse('queries')}">
				</og-button>
			</div>
		</div>
	`;

	_onMenuSelect = (e: MenuEvent) => {
		switch (e.item.key) {
			case 'new':
				this.host.openModalSidebar({id: 'extensions-projectForm', caption: msg('New project')});
				return;
			case 'properties':
				if (this.project) this.host.openModalSidebar({
					id: `extensions-projectForm-${this.project.id_custom_page}`,
					caption: `#${this.project.id_custom_page} ${this.project.name}`
				});
				return;
			case 'copy':
				if (this.project) this.host.openModalSidebar({
					id: `extensions-projectCopyForm-${this.project.id_custom_page}`,
					caption: `${msg('Copy project')}: #${this.project.id_custom_page} ${this.project.name}`
				});
				return;
			case 'access':
				rp.host.open(`users-entityUsers-${EntityAccessType.Custom}-${this.project.id_custom_page}`, { title: `${msg('Access')}: ${this.project.name}` });
				break;
			case 'externalResources':
				if (this.project) this.host.open(`extensions-externalResources-${this.project.id_custom_page}`, {
					title: e.item.value
				});
				return;
			case 'sqlDependencies':
				if (this.project) this.host.open(`extensions-sqlDependencies-${this.project.id_custom_page}`, {
					title: e.item.value
				});
				return;
			case 'sqlParameters':
				if (this.project) this.host.open(`extensions-parameterGrid-${this.project.id_custom_page}`, {
					title: e.item.value
				});
				return;
			case 'resources':
				if (this.project) this.host.open(`extensions-fileGrid-${this.project.id_custom_page}`, {
					title: e.item.value
				});
				return;
			case 'queries':
				if (this.project) this.host.open(`extensions-queryGrid-${this.project.id_custom_page}`, {
					title: e.item.value
				});
				return;
			case 'close':
				this.closeProject();
				return;
			case 'list':
				rp.host.open('extensions-projectGrid', {title: msg('Projects')});
				return;
		}
	}

	render = () => html`
		<div class="head">
			<og-tree-dropdown
				class="project-select"
				color="transparent"
				.adapter="${this.projectTreeAdapter}"
				@change="${this._onProjectChange}"
				style="flex: 1">
			</og-tree-dropdown>

			<og-dropdown-menu
				noCaret
				dynamicWidth
				icon="bars"
				color="transparent"
				@menu.select="${this._onMenuSelect}"
				.items="${[
					{key: 'new', value: msg('New project'), icon: 'plus'},
					{key: 'list', value: msg('Project list'), icon: 'table'},
					...(this.project ? [
						{key: 'close', value: msg('Close project'), icon: 'xmark', divider: true},
						// a shared or inherited project belongs to its owner company: only that one edits or copies it
						...(isForeignProject(this.project) ? [] : [
							{key: 'properties', value: msg('Project properties'), icon: 'pen-to-square'},
							{key: 'copy', value: msg('Copy project'), icon: 'copy'},
						]),
						{key: 'access', value: msg('Access'), icon: 'shield-check'},
						{key: 'externalResources', value: msg('External resources'), icon: 'books'},
						{key: 'sqlDependencies', value: msg('SQL dependencies'), icon: 'database'},
						{key: 'sqlParameters', value: msg('SQL parameters'), icon: 'input-text'},
						{key: 'resources', value: msg('File list'), icon: 'files'},
						{key: 'queries', value: msg('Query list'), icon: 'database'},
					] : []),
				]}">
			</og-dropdown-menu>
		</div>

		<div class="body">
			${this.project ? html`
				<og-accordion
					.createComponent="${(id: ComponentId) => this.browserComponentStore.get(id)}"
					.items="${this._restoreAccordionItems([
						{id: 'files', title: this.renderFilesTitle(), icon: 'code'},
						{id: 'queries', title: this.renderQueriesTitle(), icon: 'database'},
					])}">
				</og-accordion>
			` : html`
				<og-tree
					.adapter="${new ProjectTreeAdapter({newProject: true, search: false})}"
					@change="${this._onProjectChange}">
				</og-tree>
			`}
		</div>
	`;

}
